import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { 
    Plus, ClipboardList, CheckCircle, XCircle, Clock,
    Package, AlertTriangle, Truck, PackageCheck, HourglassIcon, Minus, Trash2,
    Building2, Users, Shield, History, FileDown
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusConfig = {
    pending: { label: 'En attente', badge: 'warning', icon: Clock },
    en_attente_reponse: { label: 'Attente reponse', badge: 'warning', icon: HourglassIcon },
    approved: { label: 'Approuvee', badge: 'success', icon: CheckCircle },
    refused: { label: 'Refusee', badge: 'critical', icon: XCircle },
    in_transit: { label: 'En transit', badge: 'info', icon: Truck },
    delivered: { label: 'Livree', badge: 'success', icon: PackageCheck },
    partial: { label: 'Partielle', badge: 'warning', icon: Package }
};

const categoryLabels = { interne: 'Interne', contrat_externe: 'Contrat ext.', association: 'Association' };
const categoryIcons = { interne: Package, contrat_externe: Building2, association: Users };
const categoryColors = { interne: 'text-emerald-400', contrat_externe: 'text-amber-400', association: 'text-cyan-400' };

const getTimeColor = (createdAt) => {
    const diffMin = (Date.now() - new Date(createdAt).getTime()) / 60000;
    if (diffMin <= 30) return 'bg-green-500/15 border-green-500/30';
    if (diffMin <= 90) return 'bg-amber-500/15 border-amber-500/30';
    return 'bg-red-500/15 border-red-500/30';
};

const getTimeDotColor = (createdAt) => {
    const diffMin = (Date.now() - new Date(createdAt).getTime()) / 60000;
    if (diffMin <= 30) return 'bg-green-500';
    if (diffMin <= 90) return 'bg-amber-500';
    return 'bg-red-500';
};

const MaterialRequestsPage = () => {
    const { token, user, canManageLogistics, canRequestMaterials, isGymnaseChefPoste } = useAuth();
    const [requests, setRequests] = useState([]);
    const [centers, setCenters] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [processDialogOpen, setProcessDialogOpen] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [processNotes, setProcessNotes] = useState('');
    const [processVehicleId, setProcessVehicleId] = useState('');
    const [showHistory, setShowHistory] = useState(false);
    
    const [formData, setFormData] = useState({
        center_id: '', urgency: 'normal', notes: '',
        items: [{ resource_id: '', resource_type: 'autre', resource_name: '', resource_category: '', quantity: 1, notes: '' }]
    });
    const [statusFilter, setStatusFilter] = useState('all');

    const headers = { Authorization: `Bearer ${token}` };

    const fetchData = async () => {
        try {
            const [requestsRes, centersRes, vehiclesRes, resourcesRes] = await Promise.all([
                axios.get(`${API}/material-requests`, { headers }),
                axios.get(`${API}/care-centers`, { headers }),
                axios.get(`${API}/vehicles`, { headers }),
                axios.get(`${API}/resources`, { headers })
            ]);
            setRequests(requestsRes.data);
            setCenters(centersRes.data);
            setVehicles(vehiclesRes.data);
            setResources(resourcesRes.data);
            if (isGymnaseChefPoste && user?.assigned_center_id) {
                setFormData(prev => ({ ...prev, center_id: user.assigned_center_id }));
            }
        } catch (error) {
            console.error('Failed to fetch:', error);
        } finally { setLoading(false); }
    };

    useEffect(() => { fetchData(); }, []);
    useEffect(() => { const iv = setInterval(() => { setRequests(r => [...r]); }, 30000); return () => clearInterval(iv); }, []);

    const activeVehicleIds = useMemo(() => {
        const active = ['pending', 'en_attente_reponse', 'approved', 'in_transit'];
        return new Set(requests.filter(r => active.includes(r.status) && r.assigned_vehicle_id).map(r => r.assigned_vehicle_id));
    }, [requests]);

    const availableVehicles = useMemo(() => vehicles.filter(v => v.status === 'disponible' && !activeVehicleIds.has(v.id)), [vehicles, activeVehicleIds]);

    // Group resources by category for the selector
    const groupedResources = useMemo(() => {
        const groups = { interne: [], contrat_externe: [], association: [] };
        resources.forEach(r => {
            const cat = r.category || 'interne';
            if (groups[cat]) groups[cat].push(r);
        });
        return groups;
    }, [resources]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.center_id) { toast.error('Selectionnez un centre'); return; }
        try {
            await axios.post(`${API}/material-requests`, formData, { headers });
            toast.success('Demande envoyee');
            setDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) { toast.error('Erreur'); }
    };

    const handleProcess = async (status) => {
        if (!selectedRequest) return;
        try {
            const payload = { status, process_notes: processNotes };
            if (processVehicleId && processVehicleId !== 'none') payload.assigned_vehicle_id = processVehicleId;
            await axios.put(`${API}/material-requests/${selectedRequest.id}/process`, payload, { headers });
            toast.success('Demande traitee');
            setProcessDialogOpen(false);
            setSelectedRequest(null);
            setProcessNotes('');
            setProcessVehicleId('');
            fetchData();
        } catch (error) { toast.error('Erreur'); }
    };

    const resetForm = () => {
        setFormData({
            center_id: isGymnaseChefPoste && user?.assigned_center_id ? user.assigned_center_id : '',
            urgency: 'normal', notes: '',
            items: [{ resource_id: '', resource_type: 'autre', resource_name: '', resource_category: '', quantity: 1, notes: '' }]
        });
    };

    const handleDelete = async (requestId) => {
        if (!window.confirm('Supprimer cette demande ?')) return;
        try {
            await axios.delete(`${API}/material-requests/${requestId}`, { headers });
            toast.success('Supprimee');
            fetchData();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    };

    const addItem = () => setFormData({
        ...formData,
        items: [...formData.items, { resource_id: '', resource_type: 'autre', resource_name: '', resource_category: '', quantity: 1, notes: '' }]
    });
    const removeItem = (index) => setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
    const selectResource = (index, resourceId) => {
        const newItems = [...formData.items];
        if (resourceId === 'custom') {
            newItems[index] = { ...newItems[index], resource_id: '', resource_name: '', resource_category: '', resource_type: 'autre' };
        } else {
            const res = resources.find(r => r.id === resourceId);
            if (res) {
                newItems[index] = {
                    ...newItems[index],
                    resource_id: res.id,
                    resource_name: res.name,
                    resource_category: res.category || 'interne',
                    resource_type: res.type
                };
            }
        }
        setFormData({ ...formData, items: newItems });
    };
    const updateItem = (index, field, value) => {
        const newItems = [...formData.items];
        newItems[index][field] = value;
        setFormData({ ...formData, items: newItems });
    };

    const getResourceInfo = (item) => {
        if (item.resource_id) return resources.find(r => r.id === item.resource_id);
        return null;
    };

    const pendingCount = requests.filter(r => r.status === 'pending').length;
    const waitingCount = requests.filter(r => r.status === 'en_attente_reponse').length;
    const approvedCount = requests.filter(r => r.status === 'approved').length;
    const inTransitCount = requests.filter(r => r.status === 'in_transit').length;
    const deliveredCount = requests.filter(r => r.status === 'delivered').length;
    const refusedCount = requests.filter(r => r.status === 'refused').length;

    const filteredRequests = statusFilter === 'all' ? requests : requests.filter(r => r.status === statusFilter);

    if (loading) return <div className="flex items-center justify-center h-full"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div></div>;

    return (
        <div className="space-y-6" data-testid="material-requests-page">
            <div className="flex items-center justify-between">
                <div className="page-header">
                    <h1 className="text-3xl font-bold text-white">Demandes de materiel</h1>
                    <p className="page-subtitle">Puise dans l'inventaire interne et externe</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" className="text-xs text-zinc-400 hover:text-white"
                        onClick={() => setShowHistory(true)} data-testid="show-material-history-btn">
                        <History className="w-3.5 h-3.5 mr-1" /> Historique
                    </Button>
                    {canRequestMaterials && (
                    <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                        <DialogTrigger asChild>
                            <Button className="tactical-btn" data-testid="add-request-btn">
                                <Plus className="w-4 h-4 mr-2" /> Nouvelle demande
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl bg-zinc-900 border-zinc-800 max-h-[85vh] overflow-y-auto">
                            <DialogHeader><DialogTitle className="text-xl font-semibold text-white">Nouvelle demande de materiel</DialogTitle></DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="form-group">
                                        <Label className="form-label">Centre CARE *</Label>
                                        <Select value={formData.center_id} onValueChange={(v) => setFormData({...formData, center_id: v})}>
                                            <SelectTrigger className="tactical-input" data-testid="request-center"><SelectValue placeholder="Selectionner" /></SelectTrigger>
                                            <SelectContent className="bg-zinc-900 border-zinc-800">{centers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div className="form-group">
                                        <Label className="form-label">Urgence</Label>
                                        <Select value={formData.urgency} onValueChange={(v) => setFormData({...formData, urgency: v})}>
                                            <SelectTrigger className="tactical-input" data-testid="request-urgency"><SelectValue /></SelectTrigger>
                                            <SelectContent className="bg-zinc-900 border-zinc-800">
                                                <SelectItem value="normal">Normal</SelectItem>
                                                <SelectItem value="urgent">Urgent</SelectItem>
                                                <SelectItem value="critique">Critique</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                {/* Items from inventory */}
                                <div className="space-y-2">
                                    <div className="flex items-center justify-between">
                                        <Label className="form-label">Ressources demandees</Label>
                                        <Button type="button" size="sm" variant="ghost" onClick={addItem} className="text-blue-400"><Plus className="w-3 h-3 mr-1" /> Ajouter</Button>
                                    </div>
                                    {formData.items.map((item, i) => {
                                        const resInfo = getResourceInfo(item);
                                        return (
                                            <div key={i} className="p-3 bg-zinc-800/50 rounded space-y-2" data-testid={`request-item-${i}`}>
                                                <div className="flex items-center gap-2">
                                                    <Select value={item.resource_id || 'custom'} onValueChange={(v) => selectResource(i, v)}>
                                                        <SelectTrigger className="tactical-input flex-1" data-testid={`item-resource-${i}`}>
                                                            <SelectValue placeholder="Selectionner une ressource" />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-zinc-900 border-zinc-800 max-h-[300px]">
                                                            <SelectItem value="custom">Saisie libre</SelectItem>
                                                            {Object.entries(groupedResources).map(([cat, items]) => {
                                                                if (items.length === 0) return null;
                                                                const CatIcon = categoryIcons[cat];
                                                                return (
                                                                    <React.Fragment key={cat}>
                                                                        <div className="px-2 py-1.5 text-[10px] text-zinc-500 uppercase tracking-wider flex items-center gap-1 border-t border-zinc-800 mt-1">
                                                                            <CatIcon className="w-3 h-3" /> {categoryLabels[cat]}
                                                                        </div>
                                                                        {items.map(r => (
                                                                            <SelectItem key={r.id} value={r.id}>
                                                                                <span className="flex items-center gap-2">
                                                                                    <span>{r.name}</span>
                                                                                    <span className="text-[10px] text-zinc-500">({r.quantity_available}/{r.quantity})</span>
                                                                                    {r.organization_name && <span className="text-[10px] text-zinc-500">- {r.organization_name}</span>}
                                                                                </span>
                                                                            </SelectItem>
                                                                        ))}
                                                                    </React.Fragment>
                                                                );
                                                            })}
                                                        </SelectContent>
                                                    </Select>
                                                    <Input type="number" value={item.quantity} onChange={(e) => updateItem(i, 'quantity', parseInt(e.target.value) || 1)} className="tactical-input w-20" min="1" />
                                                    {formData.items.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => removeItem(i)} className="text-red-500"><Minus className="w-3 h-3" /></Button>}
                                                </div>
                                                {!item.resource_id && (
                                                    <Input value={item.notes || ''} onChange={(e) => updateItem(i, 'notes', e.target.value)} className="tactical-input text-sm" placeholder="Decrire le besoin..." />
                                                )}
                                                {resInfo && (
                                                    <div className="flex items-center gap-3 text-[10px] text-zinc-400 flex-wrap">
                                                        <span className={`flex items-center gap-1 ${categoryColors[resInfo.category] || 'text-zinc-400'}`}>
                                                            {React.createElement(categoryIcons[resInfo.category] || Package, { className: 'w-3 h-3' })}
                                                            {categoryLabels[resInfo.category] || resInfo.category}
                                                        </span>
                                                        <span>Dispo: <span className="text-white font-mono">{resInfo.quantity_available}</span>/{resInfo.quantity}</span>
                                                        {resInfo.availability_delay && (
                                                            <span className="text-amber-400 flex items-center gap-1"><Clock className="w-3 h-3" /> Delai: {resInfo.availability_delay}</span>
                                                        )}
                                                        {resInfo.prerequisites && (
                                                            <span className="text-amber-300 flex items-center gap-1"><Shield className="w-3 h-3" /> {resInfo.prerequisites}</span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="form-group">
                                    <Label className="form-label">Notes</Label>
                                    <Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} className="tactical-input" placeholder="Commentaire..." />
                                </div>
                                <div className="flex justify-end gap-2 pt-2">
                                    <Button type="button" variant="ghost" onClick={() => { setDialogOpen(false); resetForm(); }}>Annuler</Button>
                                    <Button type="submit" className="tactical-btn" data-testid="request-submit-btn">Envoyer</Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
                </div>
            </div>

            {/* Stats - Clickable filters */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
                {[
                    { key: 'all', count: requests.length, label: 'Total', border: 'border-l-blue-500', ring: 'ring-blue-500/40' },
                    { key: 'pending', count: pendingCount, label: 'En attente', border: 'border-l-amber-500', ring: 'ring-amber-500/40' },
                    { key: 'en_attente_reponse', count: waitingCount, label: 'Att. reponse', border: 'border-l-yellow-500', ring: 'ring-yellow-500/40' },
                    { key: 'approved', count: approvedCount, label: 'Approuvees', border: 'border-l-green-500', ring: 'ring-green-500/40' },
                    { key: 'in_transit', count: inTransitCount, label: 'En transit', border: 'border-l-cyan-500', ring: 'ring-cyan-500/40' },
                    { key: 'delivered', count: deliveredCount, label: 'Livrees', border: 'border-l-teal-500', ring: 'ring-teal-500/40' },
                    { key: 'refused', count: refusedCount, label: 'Refusees', border: 'border-l-red-500', ring: 'ring-red-500/40' },
                ].map(s => (
                    <Card key={s.key}
                        className={`stat-card ${s.border} cursor-pointer transition-all hover:scale-[1.02] ${statusFilter === s.key ? `ring-2 ${s.ring}` : ''}`}
                        onClick={() => setStatusFilter(statusFilter === s.key ? 'all' : s.key)}
                        data-testid={`filter-${s.key}`}>
                        <CardContent className="p-0">
                            <p className="stat-value">{s.count}</p>
                            <p className="stat-label">{s.label}</p>
                        </CardContent>
                    </Card>
                ))}
            </div>
            {statusFilter !== 'all' && (
                <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-zinc-300 border-zinc-600">
                        Filtre : {statusConfig[statusFilter]?.label || statusFilter}
                    </Badge>
                    <Button size="sm" variant="ghost" className="text-xs text-zinc-500 h-6" onClick={() => setStatusFilter('all')}>Tout afficher</Button>
                </div>
            )}

            {/* Process Dialog */}
            <Dialog open={processDialogOpen} onOpenChange={setProcessDialogOpen}>
                <DialogContent className="max-w-md bg-zinc-900 border-zinc-800">
                    <DialogHeader><DialogTitle>Traiter la demande</DialogTitle></DialogHeader>
                    {selectedRequest && (
                        <div className="space-y-4">
                            <div className="p-3 bg-zinc-800/50 rounded">
                                <p className="font-medium text-white">{selectedRequest.center_name}</p>
                                <p className="text-sm text-zinc-400">Par {selectedRequest.requested_by_name}</p>
                                <div className="mt-2 space-y-1.5">
                                    {selectedRequest.items.map((item, i) => {
                                        const CatIcon = categoryIcons[item.resource_category] || Package;
                                        return (
                                            <div key={`${item.resource_name || item.resource_type || item.notes}-${item.quantity}`} className="flex items-start gap-2">
                                                <div className="flex-1">
                                                    <p className="text-sm">
                                                        <span className="text-white font-mono">{item.quantity}x</span>{' '}
                                                        <span className="text-zinc-300">{item.resource_name || item.resource_type || item.notes}</span>
                                                    </p>
                                                    {item.resource_category && (
                                                        <span className={`text-[10px] flex items-center gap-1 ${categoryColors[item.resource_category] || 'text-zinc-500'}`}>
                                                            <CatIcon className="w-3 h-3" /> {categoryLabels[item.resource_category] || item.resource_category}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {selectedRequest.assigned_vehicle_name && (
                                    <p className="text-xs text-cyan-400 mt-2 flex items-center gap-1"><Truck className="w-3 h-3" /> {selectedRequest.assigned_vehicle_name}</p>
                                )}
                            </div>
                            {['pending', 'approved', 'en_attente_reponse'].includes(selectedRequest.status) && (
                                <div className="form-group">
                                    <Label className="form-label">Vehicule d'acheminement</Label>
                                    <Select value={processVehicleId || 'none'} onValueChange={setProcessVehicleId}>
                                        <SelectTrigger className="tactical-input"><SelectValue placeholder="Affecter un vehicule" /></SelectTrigger>
                                        <SelectContent className="bg-zinc-900 border-zinc-800">
                                            <SelectItem value="none">Aucun</SelectItem>
                                            {availableVehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.name} - {v.plate_number}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            <div className="form-group">
                                <Label className="form-label">Notes</Label>
                                <Textarea value={processNotes} onChange={(e) => setProcessNotes(e.target.value)} className="tactical-input" placeholder="Commentaires..." data-testid="process-notes" />
                            </div>
                            <div className="flex flex-wrap justify-end gap-2">
                                <Button variant="ghost" onClick={() => setProcessDialogOpen(false)}>Annuler</Button>
                                {selectedRequest.status === 'pending' && (
                                    <>
                                        <Button onClick={() => handleProcess('en_attente_reponse')} className="bg-yellow-600 hover:bg-yellow-700 text-white" data-testid="process-waiting-btn"><HourglassIcon className="w-4 h-4 mr-1" /> Att. reponse</Button>
                                        <Button onClick={() => handleProcess('refused')} className="bg-red-600 hover:bg-red-700 text-white" data-testid="process-refuse-btn"><XCircle className="w-4 h-4 mr-1" /> Refuser</Button>
                                        <Button onClick={() => handleProcess('approved')} className="bg-green-600 hover:bg-green-700 text-white" data-testid="process-approve-btn"><CheckCircle className="w-4 h-4 mr-1" /> Approuver</Button>
                                    </>
                                )}
                                {selectedRequest.status === 'en_attente_reponse' && (
                                    <>
                                        <Button onClick={() => handleProcess('refused')} className="bg-red-600 hover:bg-red-700 text-white"><XCircle className="w-4 h-4 mr-1" /> Refuser</Button>
                                        <Button onClick={() => handleProcess('approved')} className="bg-green-600 hover:bg-green-700 text-white"><CheckCircle className="w-4 h-4 mr-1" /> Approuver</Button>
                                    </>
                                )}
                                {selectedRequest.status === 'approved' && (
                                    <Button onClick={() => handleProcess('in_transit')} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="process-transit-btn"><Truck className="w-4 h-4 mr-1" /> Expedier</Button>
                                )}
                                {selectedRequest.status === 'in_transit' && (
                                    <Button onClick={() => handleProcess('delivered')} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="process-deliver-btn"><PackageCheck className="w-4 h-4 mr-1" /> Livree</Button>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Table */}
            <Card className="content-card">
                <CardHeader className="card-header">
                    <CardTitle className="card-title flex items-center gap-2"><ClipboardList className="w-5 h-5 text-purple-500" /> Liste des demandes</CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-zinc-800 hover:bg-transparent">
                                <TableHead className="text-zinc-400">Centre</TableHead>
                                <TableHead className="text-zinc-400">Demandeur</TableHead>
                                <TableHead className="text-zinc-400">Articles</TableHead>
                                <TableHead className="text-zinc-400">Source</TableHead>
                                <TableHead className="text-zinc-400">Urgence</TableHead>
                                <TableHead className="text-zinc-400 hidden md:table-cell">Vehicule</TableHead>
                                <TableHead className="text-zinc-400">Statut</TableHead>
                                <TableHead className="text-zinc-400 hidden sm:table-cell">Date</TableHead>
                                <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredRequests.map(request => {
                                const config = statusConfig[request.status] || statusConfig.pending;
                                const StatusIcon = config.icon;
                                const canProcess = canManageLogistics && ['pending', 'en_attente_reponse', 'approved', 'in_transit'].includes(request.status);
                                const timeColor = getTimeColor(request.created_at);
                                const dotColor = getTimeDotColor(request.created_at);
                                // Determine source type
                                const itemCategories = [...new Set(request.items.map(it => it.resource_category).filter(Boolean))];
                                const isDone = request.status === 'delivered' || request.status === 'refused';
                                return (
                                    <TableRow key={request.id} className={`border-zinc-800 border ${isDone ? 'opacity-40' : timeColor}`} data-testid={`request-row-${request.id}`}>
                                        <TableCell className="font-medium text-white">{request.center_name}</TableCell>
                                        <TableCell className="text-zinc-400 text-sm">{request.requested_by_name}</TableCell>
                                        <TableCell>
                                            <div className="space-y-0.5">
                                                {request.items.slice(0, 2).map((item) => (
                                                    <p key={item.resource_id || item.resource_name || item.resource_type || item.notes} className="text-xs">
                                                        <span className="text-white font-mono">{item.quantity}x</span>{' '}
                                                        <span className="text-zinc-300">{item.resource_name || item.resource_type || item.notes}</span>
                                                    </p>
                                                ))}
                                                {request.items.length > 2 && <p className="text-xs text-zinc-500">+{request.items.length - 2} autres</p>}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {itemCategories.length > 0 ? (
                                                <div className="flex flex-col gap-0.5">
                                                    {itemCategories.map(cat => {
                                                        const CatIcon = categoryIcons[cat] || Package;
                                                        return (
                                                            <span key={cat} className={`text-[10px] flex items-center gap-1 ${categoryColors[cat] || 'text-zinc-500'}`}>
                                                                <CatIcon className="w-3 h-3" /> {categoryLabels[cat]}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            ) : <span className="text-zinc-600 text-xs">-</span>}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`status-badge ${request.urgency === 'critique' ? 'critical' : request.urgency === 'urgent' ? 'warning' : 'info'}`}>{request.urgency}</span>
                                        </TableCell>
                                        <TableCell className="hidden md:table-cell">
                                            {request.assigned_vehicle_name ? (
                                                <span className="text-cyan-400 text-xs flex items-center gap-1"><Truck className="w-3 h-3" /> {request.assigned_vehicle_name}</span>
                                            ) : <span className="text-zinc-600 text-xs">-</span>}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`status-badge ${config.badge} flex items-center gap-1 w-fit`} data-testid={`request-status-${request.id}`}>
                                                <StatusIcon className="w-3 h-3" /> {config.label}
                                            </span>
                                        </TableCell>
                                        <TableCell className="hidden sm:table-cell">
                                            <div className="flex items-center gap-2">
                                                {request.status !== 'delivered' && request.status !== 'refused' && <span className={`w-2 h-2 rounded-full ${dotColor} animate-pulse`}></span>}
                                                <span className="text-zinc-400 font-mono text-xs">
                                                    {new Date(request.created_at).toLocaleString('fr-FR', {day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit'})}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                {canProcess && (
                                                    <Button size="sm" onClick={() => { setSelectedRequest(request); setProcessVehicleId(request.assigned_vehicle_id || ''); setProcessDialogOpen(true); }} className="tactical-btn text-xs" data-testid={`process-btn-${request.id}`}>
                                                        {request.status === 'pending' && 'Traiter'}
                                                        {request.status === 'en_attente_reponse' && 'Repondre'}
                                                        {request.status === 'approved' && 'Expedier'}
                                                        {request.status === 'in_transit' && 'Livraison'}
                                                    </Button>
                                                )}
                                                {canManageLogistics && (
                                                    <Button size="sm" variant="ghost" onClick={() => handleDelete(request.id)} className="text-red-500 hover:text-red-400" data-testid={`delete-request-${request.id}`}>
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {filteredRequests.length === 0 && (
                                <TableRow><TableCell colSpan={9} className="text-center py-8 text-zinc-500"><ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-50" /> {statusFilter !== 'all' ? 'Aucune demande avec ce statut' : 'Aucune demande'}</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* History Dialog */}
            <Dialog open={showHistory} onOpenChange={setShowHistory}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-3xl max-h-[80vh]" data-testid="material-history-dialog">
                    <DialogHeader>
                        <div className="flex items-center justify-between w-full">
                            <DialogTitle className="flex items-center gap-2">
                                <History className="w-5 h-5 text-cyan-500" />
                                Historique des demandes de materiel
                            </DialogTitle>
                            {requests.length > 0 && (
                                <a href={`${API}/material-requests/history/export?token=${token}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium bg-cyan-600/20 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-600/30 transition-colors"
                                    data-testid="export-material-history-btn">
                                    <FileDown className="w-3.5 h-3.5" /> Exporter CSV
                                </a>
                            )}
                        </div>
                    </DialogHeader>
                    <div className="overflow-y-auto max-h-[60vh] mt-2">
                        {requests.length > 0 ? (
                            <div className="space-y-2">
                                {requests.map((r) => {
                                    const config = statusConfig[r.status] || statusConfig.pending;
                                    const StatusIcon = config.icon;
                                    return (
                                        <div key={r.id} className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700/50">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-sm font-medium text-white">{r.center_name}</span>
                                                    <span className="text-zinc-500 text-xs">par {r.requested_by_name}</span>
                                                    <Badge className={`text-[10px] ${
                                                        ['approved', 'delivered'].includes(r.status) ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' :
                                                        r.status === 'refused' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                                                        'bg-amber-500/20 text-amber-400 border-amber-500/30'
                                                    }`}>
                                                        <StatusIcon className="w-3 h-3 mr-1" /> {config.label}
                                                    </Badge>
                                                </div>
                                                <span className="text-[10px] text-zinc-500">
                                                    {new Date(r.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            </div>
                                            <p className="text-xs text-zinc-400 mt-1">
                                                {r.items.map(it => `${it.quantity}x ${it.resource_name || it.resource_type}`).join(', ')}
                                            </p>
                                            {r.processed_by_name && (
                                                <p className="text-[10px] text-zinc-500 mt-1">
                                                    Traite par {r.processed_by_name}{r.process_notes ? ` — ${r.process_notes}` : ''}
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="text-zinc-500 text-sm text-center py-8">Aucun historique</p>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MaterialRequestsPage;
